import VariantLensAPIClient from '../auth/VariantLensAPIClient'

/**
 * Analysis-samples client (GEN-5674).
 *
 * Consumes the variant-lens-api GET /analyses/{token}/samples endpoint
 * (GEN-5673), which resolves a pasted SeqOne analysis display name, analysis
 * UUID, or order token to that analysis's member samples via MCDB + SeqOne. The
 * API owns all identifier resolution; this module only types the response and
 * maps transport outcomes, so no resolution grammar is duplicated client-side.
 *
 * Like the order catalog (GEN-5467), resolution is inert (503) until the MCDB /
 * SeqOne environment is configured (GEN-5438), so "unavailable" is an expected
 * state surfaced as a distinct outcome rather than an error. The endpoint also
 * returns 409 when a token matches more than one analysis on the order, with the
 * candidate analysis ids the caller can offer as a chooser.
 */

/** One member sample of a resolved analysis. Mirrors the API's AnalysisSampleModel. */
export type AnalysisSample = {
  sampleId: string
  name: string
  labSampleId: string | null
  role: string | null
  sex: string | null
}

/** A resolved analysis and its member samples. Mirrors AnalysisSamplesResponse. */
export type AnalysisSamplesResult = {
  analysisId: string
  analysisName: string
  orderId: string
  samples: AnalysisSample[]
}

export type AnalysisSamplesOutcome =
  | { kind: 'ok'; result: AnalysisSamplesResult }
  | { kind: 'unavailable' }
  | { kind: 'ambiguous'; candidateAnalysisIds: string[] }
  | { kind: 'notFound' }
  | { kind: 'invalid'; message: string }
  | { kind: 'aborted' }
  | { kind: 'error'; message: string }

type RawSample = {
  sample_id?: unknown
  name?: unknown
  lab_sample_id?: unknown
  role?: unknown
  sex?: unknown
}

type RawResult = {
  analysis_id?: unknown
  analysis_name?: unknown
  order_id?: unknown
  samples?: unknown
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null
}

function parseSample(raw: RawSample): AnalysisSample | null {
  const sampleId = asString(raw.sample_id)
  // A sample with no id cannot open a case; drop it rather than render a broken,
  // unopenable row. The API always sets it, so this is defensive.
  if (!sampleId) return null
  return {
    sampleId,
    name: asString(raw.name) ?? sampleId,
    labSampleId: asString(raw.lab_sample_id),
    role: asString(raw.role),
    sex: asString(raw.sex),
  }
}

function parseResult(raw: RawResult): AnalysisSamplesResult {
  const rawSamples = Array.isArray(raw.samples) ? (raw.samples as RawSample[]) : []
  const samples = rawSamples.map(parseSample).filter((s): s is AnalysisSample => s !== null)
  return {
    analysisId: asString(raw.analysis_id) ?? '',
    analysisName: asString(raw.analysis_name) ?? '',
    orderId: asString(raw.order_id) ?? '',
    samples,
  }
}

function parseCandidateIds(body: unknown): string[] {
  // The API's 409 detail is { message, candidate_analysis_ids: [...] }. Read the
  // ids defensively so a shape change degrades to an empty chooser, not a crash.
  const detail = (body as { detail?: unknown } | null)?.detail
  const ids = (detail as { candidate_analysis_ids?: unknown } | null)?.candidate_analysis_ids
  return Array.isArray(ids) ? ids.filter((v): v is string => typeof v === 'string') : []
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json()
  } catch {
    return null
  }
}

/**
 * Resolve a pasted analysis token to its member samples. Never throws for an
 * expected dependency or resolution condition: a 503 maps to `unavailable`, a
 * 409 to `ambiguous` (with candidate ids), a 404 to `notFound`, a 422 to
 * `invalid`; other non-2xx and transport failures map to `error`, so the UI can
 * render a calm state rather than crash.
 */
export async function fetchAnalysisSamples(
  apiClient: VariantLensAPIClient,
  token: string,
  signal?: AbortSignal
): Promise<AnalysisSamplesOutcome> {
  const path = `/analyses/${encodeURIComponent(token.trim())}/samples`
  try {
    const response = await apiClient.get(path, undefined, true, signal)
    if (response.status === 503) return { kind: 'unavailable' }
    if (response.status === 409) {
      return { kind: 'ambiguous', candidateAnalysisIds: parseCandidateIds(await readJson(response)) }
    }
    if (response.status === 404) return { kind: 'notFound' }
    if (response.status === 422) {
      const body = (await readJson(response)) as { detail?: unknown } | null
      const message = typeof body?.detail === 'string' ? body.detail : 'That analysis id is not valid.'
      return { kind: 'invalid', message }
    }
    if (!response.ok) return { kind: 'error', message: `Analysis lookup failed (${response.status})` }
    return { kind: 'ok', result: parseResult((await response.json()) as RawResult) }
  } catch (e) {
    if (signal?.aborted) return { kind: 'aborted' }
    return { kind: 'error', message: e instanceof Error ? e.message : 'Could not resolve the analysis' }
  }
}

/** Route to open a resolved member sample as a case. */
export function sampleCasePath(sample: AnalysisSample): string {
  return `/cases/${encodeURIComponent(sample.sampleId)}`
}
